import microuniversoApi from "app/api/microuniversoApi";
import { Breadcrumb2Container } from "app/components/breadcrumb2/Breadcrumb2Container";
import { IItemOrcamento } from "app/types/IItemOrcamento";
import { ILotesReservados } from "app/types/ILotesReservados";
import globalMU from "globalMU";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { LotesReservados } from "./LotesReservados";
import { obterParametros } from "app/use-cases/parametro/obterParametros";
import { Parametro } from "app/types/Parametro";
import { tratamentoErroPadrao } from "app/use-cases";
import { Filial } from "app/components/movimentacao-estoque/MovimentacaoEstoque";
import { Dict } from "app/utils/Dict";
import { useMenuConsultaProduto } from "app/components/menu-consulta-produtos/useMenuConsultaProduto";

export type LotesReservadosContainerProps = {
    item: IItemOrcamento,
    onVoltar: () => void,
    onRemover: () => void,
    passos: Dict<string, () => void>,
    foraOrcamento: boolean
};

export const LotesReservadosContainer = (props: LotesReservadosContainerProps) => {
    const [dadosLotesReservados, setDadosLotesReservados] = useState<ILotesReservados>();
    const [parametros, setParametros] = useState<Parametro[]>([]);
    const [filiais, setFiliais] = useState<Filial[]>()
    const [filialSelecionada, setFilialSelecionada] = useState("")

    const isMounted = useRef(true)
    const passosInterno = useRef<Dict<string, () => void>>(new Dict(Object.assign({}, props.passos.map)))

    useMenuConsultaProduto(new Dict(Object.assign({}, props.passos.map)), props.onVoltar, props.item, props.foraOrcamento)

    const valorParametro = useCallback(
        (nome: string) => parametros.find((p) => p.Nome == nome)?.Valor,
        [parametros]
    )

    const onVoltar = useCallback(() => {
        props.onRemover()
        props.onVoltar()
    }, [props.onRemover, props.onVoltar])

    const onObterDadosLotesReservados = async (numeroPagina?: number, filial?: string, voltar?: boolean) => {
        globalMU.blockUI();
        try {
            const lotesReservados = await microuniversoApi.Produto.listarLotesReservados({
                CodigoProduto: props.item.Codigo,
                Filial: filial ?? null,
                NumeroPagina: numeroPagina ?? 1,
                TamanhoPagina: 20
            });

            if (!isMounted.current) return

            // lotesReservados == null PRIMEIRO: globalMU.tratarRetorno devolve null quando o servidor
            // responde com erro que não é DomainLayerException (a mensagem já foi mostrada por ele).
            // Sem essa checagem, `"HasErrors" in null` estoura
            // "TypeError: Cannot use 'in' operator to search for 'HasErrors' in null".
            if (lotesReservados == null || "HasErrors" in lotesReservados) {
                setDadosLotesReservados({})
                if (voltar) onVoltar()
            } else {
                setDadosLotesReservados(lotesReservados)
            }
        }
        catch (e) {
            tratamentoErroPadrao(e);
            filial == undefined && numeroPagina == undefined && onVoltar()
        }
        finally {
            globalMU.unblockUI();
        }
    }

    const onListarFiliais = async () => {
        globalMU.blockUI();
        try {
            const dadosFiliais = await microuniversoApi.Produto.listarFiliais();

            if (isMounted.current) {
                setFiliais(dadosFiliais)
            }
        }
        catch (e) {
            tratamentoErroPadrao(e);
        }
        finally {
            globalMU.unblockUI();
        }
    }

    const onPassoSelecionado = useCallback((passo: string) => {
        props.onRemover()
        passosInterno.current.get(passo)()
    }, [props.onRemover])

    const onPesquisarPorFilial = useCallback((_numeroPagina: number, filial: string) => {
        // Troca de filial sempre volta para a página 1 — manter a página anterior podia cair fora do
        // total de páginas do novo filtro e devolver lista vazia.
        setFilialSelecionada(filial)
        onObterDadosLotesReservados(1, filial)
    }, [props.item.Codigo])

    const onPesquisarPaginado = useCallback((numeroPagina: number) => {
        // A filial escolhida PRECISA ser reenviada aqui. Antes o container passava a mesma função
        // (onObterDadosLotesReservados) para os dois callbacks, e a paginação chamava com o segundo
        // parâmetro undefined -> Filial: null -> virar de página descartava silenciosamente o filtro
        // de filial e voltava a listar todas.
        onObterDadosLotesReservados(numeroPagina, filialSelecionada || null)
    }, [filialSelecionada, props.item.Codigo])

    useEffect(() => {
        isMounted.current = true

        const carregar = async () => {
            // O rótulo do passo sai do RETORNO do await, não do state `parametros`: antes o
            // valorParametro era lido na PRIMEIRA linha do efeito, ANTES de atualizarValoresParametros()
            // ser chamado (duas linhas depois) — então nunca era "S" e o passo era sempre
            // "Lotes Reservados", em qualquer cliente.
            const parametrosObtidos = await obterParametros({ nomes: ["CONTROLE_VALIDADE_PRODUTOS"] })

            if (!isMounted.current) return

            setParametros(parametrosObtidos)

            const usaControleValidade =
                parametrosObtidos.find((p) => p.Nome == "CONTROLE_VALIDADE_PRODUTOS")?.Valor == "S"

            passosInterno.current.set(
                usaControleValidade ? "Lotes/Validade Reservados" : "Lotes Reservados",
                () => { }
            )

            await Promise.all([
                onObterDadosLotesReservados(null, null, true),
                onListarFiliais(),
            ])
        }

        carregar()

        return () => {
            isMounted.current = false
        }
    }, [])

    return (
        <>
            <Breadcrumb2Container
                passos={passosInterno.current.keysToList()}
                id="LotesReservadosContainer"
                exibirBotaoLimpar={false}
                exibirBotaoVoltar={true}
                onPassoSelecionado={onPassoSelecionado}
                onBotaoVoltarClicado={onVoltar}
            />
            <LotesReservados
                produto={props.item.CodigoProdutoExibicao && props.item.CodigoProdutoExibicao.trimEnd() != "" ? props.item.CodigoProdutoExibicao.trimEnd() : props.item.CodigoProduto.trimEnd()}
                descricao={props.item.Descricao}
                dados={dadosLotesReservados}
                filiais={filiais}
                filialSelecionada={filialSelecionada}
                permiteVisualizarValidade={valorParametro("CONTROLE_VALIDADE_PRODUTOS") == "S"}
                onPesquisarPorFilial={onPesquisarPorFilial}
                onPesquisarPaginado={onPesquisarPaginado}
            />
        </>
    )
}
